import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

export default function GoogleAIStudioLoginDialog({ service, onClose, onSaved }) {
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const profile = service.profile;
  const isQwen = profile === "qwen-web";
  const gesture = useRef(null);

  function position(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(view.width - 1, Math.floor((event.clientX - rect.left) * view.width / rect.width))),
      y: Math.max(0, Math.min(view.height - 1, Math.floor((event.clientY - rect.top) * view.height / rect.height))),
    };
  }

  function startPointer(event) {
    if (!isQwen || busy || event.button !== 0 || !event.isPrimary) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    gesture.current = { id: event.pointerId, points: [position(event)], moved: false };
  }

  function movePointer(event) {
    const active = gesture.current;
    if (!active || active.id !== event.pointerId) return;
    const point = position(event);
    const first = active.points[0];
    active.moved ||= Math.hypot(point.x - first.x, point.y - first.y) >= 4;
    if (active.points.length >= 159) active.points = active.points.filter((_, index) => index % 2 === 0);
    active.points.push(point);
  }

  function finishPointer(event) {
    const active = gesture.current;
    if (!active || active.id !== event.pointerId) return;
    movePointer(event);
    gesture.current = null;
    if (active.moved) action({ action: "drag", points: active.points });
    else action({ action: "click", ...active.points[0] });
  }

  function cancelPointer(event) {
    if (gesture.current?.id === event.pointerId) gesture.current = null;
  }

  const providerName = isQwen ? "Qwen" : "Google AI Studio";
  const loginHost = isQwen ? "chat.qwen.ai" : "accounts.google.com";
  const action = async (body) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/browser/profiles/${encodeURIComponent(profile)}/manual`, body, { timeout: 45000 });
      if (data.saved) {
        toast.success("Sesión de " + providerName + " guardada"); onSaved(); onClose();
      } else if (data.image_b64) setView(data);
    } catch (e) { setError(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { action({ action: "start" }); }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => {
    api.post(`/browser/profiles/${encodeURIComponent(profile)}/manual`, { action: "close" }).catch(() => {});
    setText(""); onClose();
  };
  return <Dialog open onOpenChange={(open) => !open && close()}>
    <DialogContent className="bg-zinc-950 border-zinc-800 w-[95vw] max-w-6xl max-h-[95vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Iniciar sesión en {providerName} · {profile}</DialogTitle></DialogHeader>
      <p className="text-sm text-zinc-400">Este navegador pertenece solo a este perfil. Pulsa el campo de {providerName} en la imagen, escribe abajo y pulsa «Introducir texto». Completa tú el acceso y cualquier verificación.</p>
      <div className="flex flex-wrap gap-2 items-center">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => action({ action: view ? "view" : "start" })}>{busy ? "Cargando…" : "Actualizar vista"}</Button>
        <span className="text-xs text-zinc-400">{view?.host || loginHost}</span>
        <Button size="sm" className="ml-auto" disabled={busy || !view} onClick={() => action({ action: "save" })}>Guardar sesión</Button>
      </div>
      {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
      {view && <img alt={"Navegador privado de " + providerName + ": pulsa para interactuar"} draggable={false}
        className={"w-full rounded border border-zinc-700 " + (busy ? "opacity-60 pointer-events-none" : "cursor-pointer")}
        src={`data:image/jpeg;base64,${view.image_b64}`}
        style={isQwen ? { touchAction: "none", userSelect: "none" } : undefined}
        onPointerDown={startPointer} onPointerMove={movePointer} onPointerUp={finishPointer}
        onPointerCancel={cancelPointer} onLostPointerCapture={cancelPointer}
        onClick={isQwen ? undefined : (event) => action({ action: "click", ...position(event) })} />}
      {isQwen && view && <p className="text-xs text-zinc-400">Para arrastrar, mantén pulsado y mueve el puntero en la imagen. La vista se actualiza al soltar.</p>}
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (!text || busy) return; const value=text; setText(""); action({ action:"type", text:value }); }}>
        <Input type="password" autoComplete="off" maxLength={1000} aria-label={"Texto para el campo seleccionado de " + providerName} placeholder="Usuario, contraseña o código para el campo seleccionado" value={text} onChange={(e) => setText(e.target.value)} />
        <Button type="submit" disabled={busy || !view || !text}>Introducir texto</Button>
      </form>
      <div className="flex flex-wrap gap-2">
        {[["Enter","Continuar / Enter"],["Tab","Siguiente campo"],["Control+A","Seleccionar todo"],["Backspace","Borrar"],["Escape","Esc" ]].map(([key,label]) => <Button key={key} size="sm" variant="outline" disabled={busy || !view} onClick={() => action({ action:"key",key })}>{label}</Button>)}
        <Button size="sm" variant="outline" disabled={busy || !view} onClick={() => action({ action:"scroll",direction:"down" })}>Bajar</Button>
        <Button size="sm" variant="outline" disabled={busy || !view} onClick={() => action({ action:"scroll",direction:"up" })}>Subir</Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={close}>Cerrar acceso manual</Button>
      </div>
      <p className="text-xs text-zinc-500">La contraseña se envía al campo seleccionado del navegador y no se guarda en KRAI Connect. Solo se conserva la sesión cuando pulses «Guardar sesión». Completa el acceso hasta ver el chat de {providerName}. Guardar la sesión no activa permisos de generación ni facturación.</p>
    </DialogContent>
  </Dialog>;
}
